/**
 * Client-side utility to compute face descriptors from base64 images using face-api.
 * Used during interview booking to store face descriptors in Supabase.
 * Must only be called from browser (e.g. in handleStage2Complete).
 */

const MODELS_PATH = '/face-models';

let modelsLoaded = false;
let faceapiModule: typeof import('@vladmandic/face-api') | null = null;

async function loadModels() {
  if (modelsLoaded && faceapiModule) return;
  if (typeof window === 'undefined') {
    throw new Error('face descriptor utilities can only run in the browser');
  }
  const faceapi = await import('@vladmandic/face-api');
  await Promise.all([
    faceapi.nets.ssdMobilenetv1.loadFromUri(MODELS_PATH),
    faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_PATH),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_PATH),
  ]);
  faceapiModule = faceapi;
  modelsLoaded = true;
}

/**
 * Preloads face-api networks used for descriptor generation.
 * Keeps existing caching behavior (runs once per page load).
 */
export async function preloadFaceModels(): Promise<void> {
  await loadModels();
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}

/**
 * Computes 128-dimensional face descriptors from base64-encoded images.
 * Skips images where no face is detected.
 * @param images Array of base64 data URLs (e.g. from canvas.toDataURL('image/jpeg'))
 * @returns Array of descriptor arrays (each 128 floats)
 */
export async function computeDescriptorsFromImages(
  images: string[],
): Promise<number[][]> {
  await loadModels();
  const faceapi = faceapiModule ?? (await import('@vladmandic/face-api'));
  const descriptors: number[][] = [];

  for (const dataUrl of images) {
    try {
      const img = await loadImage(dataUrl);
      const detection = await faceapi
        .detectSingleFace(img, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detection?.descriptor) {
        descriptors.push(Array.from(detection.descriptor));
      }
    } catch (err) {
      console.warn('[face-descriptors] Failed to extract descriptor from image:', err);
    }
  }

  return descriptors;
}

type LandmarkPoint = { x: number; y: number };

function computeYawPitchFromLandmarks(landmarks: { getNose: () => LandmarkPoint[]; getLeftEye: () => LandmarkPoint[]; getRightEye: () => LandmarkPoint[]; getMouth: () => LandmarkPoint[]; }): {
  yaw: number;
  pitch: number;
} {
  const nose = landmarks.getNose();
  const leftEye = landmarks.getLeftEye();
  const rightEye = landmarks.getRightEye();
  const mouth = landmarks.getMouth();

  if (!nose.length || !leftEye.length || !rightEye.length || !mouth.length) {
    return { yaw: 0, pitch: 0 };
  }

  const noseTip = nose[3] ?? nose[nose.length - 1];
  const leftEyeCenter = leftEye.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 },
  );
  leftEyeCenter.x /= leftEye.length;
  leftEyeCenter.y /= leftEye.length;

  const rightEyeCenter = rightEye.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 },
  );
  rightEyeCenter.x /= rightEye.length;
  rightEyeCenter.y /= rightEye.length;

  const eyesMidpoint = {
    x: (leftEyeCenter.x + rightEyeCenter.x) / 2,
    y: (leftEyeCenter.y + rightEyeCenter.y) / 2,
  };

  const mouthCenter = mouth.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 },
  );
  mouthCenter.x /= mouth.length;
  mouthCenter.y /= mouth.length;

  const eyeMouthMidpoint = {
    x: (eyesMidpoint.x + mouthCenter.x) / 2,
    y: (eyesMidpoint.y + mouthCenter.y) / 2,
  };

  // Yaw: horizontal deviation of nose from eye midpoint
  const yawRadians = Math.atan2(noseTip.x - eyesMidpoint.x, Math.abs(noseTip.y - eyesMidpoint.y) + 1e-6);
  // Pitch: vertical deviation of nose from eye-mouth midpoint
  const pitchRadians = Math.atan2(noseTip.y - eyeMouthMidpoint.y, Math.abs(noseTip.x - eyeMouthMidpoint.x) + 1e-6);

  const yaw = (yawRadians * 180) / Math.PI;
  const pitch = (pitchRadians * 180) / Math.PI;

  return { yaw, pitch };
}

/**
 * Detects faces from a live HTMLVideoElement and returns descriptor + yaw/pitch
 * for the primary face, along with the number of faces detected.
 * Returns faceDetected=false when no face is found.
 */
export async function detectFaceInVideoElement(
  videoEl: HTMLVideoElement,
): Promise<{
  descriptor: number[] | null;
  yaw: number;
  pitch: number;
  faceDetected: boolean;
  faceCount: number;
}> {
  if (!videoEl || videoEl.readyState < 2) {
    return { descriptor: null, yaw: 0, pitch: 0, faceDetected: false, faceCount: 0 };
  }

  await loadModels();
  const faceapi = faceapiModule ?? (await import('@vladmandic/face-api'));

  const detections = await faceapi
    .detectAllFaces(videoEl, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (!detections.length) {
    return { descriptor: null, yaw: 0, pitch: 0, faceDetected: false, faceCount: 0 };
  }
  const primary = detections[0];
  if (!primary || !primary.landmarks) {
    return { descriptor: null, yaw: 0, pitch: 0, faceDetected: false, faceCount: 0 };
  }

  const { yaw, pitch } = computeYawPitchFromLandmarks(primary.landmarks as any);
  const faceCount = detections.length;

  return {
    descriptor: primary.descriptor ? Array.from(primary.descriptor) : null,
    yaw,
    pitch,
    faceDetected: true,
    faceCount,
  };
}

